/**
 * Las pantallas del cuestionario, una por paso.
 *
 * Cada una recibe lo que hay guardado de su paso y una función para guardar lo
 * que cambie. Ninguna conoce la sesión ni la API: eso lo lleva el contenedor.
 *
 * Todas trabajan con parches, no con el objeto entero. Es lo que hace posible
 * el guardado automático sin carreras: dos campos editados casi a la vez se
 * fusionan en el servidor en lugar de pisarse.
 */

import {
  Campo,
  Casilla,
  Deslizador,
  Escala,
  Numero,
  Seleccion,
  SeleccionMultiple,
  Texto,
} from "./fields";
import { useRef, useState } from "react";

import type { WizardContent } from "../../lib/sessions";

export interface StepProps {
  content: WizardContent;
  value: Record<string, any>;
  save: (patch: Record<string, unknown>) => void;
}

/**
 * Copia local de una respuesta compuesta (la lista de un instrumento, el mapa
 * de palancas), que se actualiza en el mismo instante del clic.
 *
 * Sin esto se pierden respuestas, y de la peor manera: en silencio. El valor
 * que llega por `value` es el del último render, y el guardado automático
 * espera 800 ms antes de escribir. Quien conteste dos preguntas seguidas en
 * menos de ese tiempo —lo normal en un test de 48 ítems— hace que la segunda
 * parta del mismo array que la primera y la sobrescriba. Medido: de 18
 * respuestas encadenadas sobrevivía 1.
 *
 * La referencia es lo que lo arregla: cada clic parte de lo que dejó el
 * anterior, aunque React todavía no haya vuelto a pintar.
 */
function useValorLocal<T>(inicial: T, claveDePaso: string) {
  const ref = useRef<T>(inicial);
  const [, repintar] = useState(0);
  const sembrado = useRef(claveDePaso);

  // Se resiembra solo al cambiar de paso, no cuando llega la confirmación del
  // servidor: reaccionar a eso devolvería el control al valor remoto y
  // desharía lo que se esté contestando.
  if (sembrado.current !== claveDePaso) {
    sembrado.current = claveDePaso;
    ref.current = inicial;
  }

  /**
   * Se actualiza con una función, nunca con un valor ya calculado.
   *
   * Es la diferencia que arregla el fallo. Un manejador de clic captura la
   * variable del render en el que se creó; si treinta clics ocurren antes de
   * que React vuelva a pintar, los treinta parten del mismo array de partida y
   * el último borra a los veintinueve anteriores. Recibiendo la función, cada
   * uno parte de lo que dejó el anterior.
   */
  const actualizar = (fn: (actual: T) => T): T => {
    ref.current = fn(ref.current);
    repintar((n) => n + 1);
    return ref.current;
  };

  return [ref.current, actualizar] as const;
}

const seccion = "space-y-5";

function Enunciado({ children }: { children: React.ReactNode }) {
  return <p className="text-base leading-relaxed text-ink">{children}</p>;
}

/**
 * La escala de acuerdo del banco.
 *
 * `noUncheckedIndexedAccess` obliga a tratar el caso de que falte, y con razón:
 * un banco sin escala dejaría los botones sin pintar y el cuestionario sería
 * incontestable. Se resuelve una vez y se falla aquí en lugar de propagar el
 * `undefined` por cada pregunta.
 */
function escalaDe(content: WizardContent, nombre?: string | null) {
  const escala = (nombre ? content.scales[nombre] : undefined) ?? Object.values(content.scales)[0];
  return escala ?? { min: 1, max: 5, options: [] };
}

// ── 1. Consentimientos ───────────────────────────────────────────────────────

export function PasoConsent({ value, save }: StepProps) {
  return (
    <div className={seccion}>
      <p className="text-ink/70">
        Antes de empezar necesitamos tu permiso explícito para tratar lo que nos cuentes. Puedes
        retirarlo cuando quieras y pedir que borremos todo.
      </p>
      <Casilla
        checked={Boolean(value.cv_processing)}
        onChange={(v) => save({ cv_processing: v })}
      >
        Autorizo el análisis de mi CV y de la información profesional que aporte.
      </Casilla>
      <Casilla
        checked={Boolean(value.ai_analysis_ack)}
        onChange={(v) => save({ ai_analysis_ack: v })}
      >
        Entiendo que se usará inteligencia artificial para redactar parte del informe. El
        diagnóstico y las puntuaciones los calcula un motor determinista, no el modelo.
      </Casilla>
      <Casilla
        checked={Boolean(value.export_delete_ack)}
        onChange={(v) => save({ export_delete_ack: v })}
      >
        Sé que puedo exportar o eliminar mis datos en cualquier momento.
      </Casilla>
      <Campo
        label="Cuánto tiempo conservamos tus respuestas"
        hint="Pasado ese plazo se eliminan automáticamente. El informe que hayas recibido se conserva."
      >
        <Numero
          value={value.data_retention_days ?? 30}
          onChange={(v) => save({ data_retention_days: v })}
          min={7}
          max={365}
          suffix="días"
        />
      </Campo>
    </div>
  );
}

// ── 2. CV ────────────────────────────────────────────────────────────────────

export function PasoCv({ value, save }: StepProps) {
  return (
    <div className={seccion}>
      <p className="text-ink/70">
        Tu trayectoria sirve para contrastar lo que dices que quieres con lo que has hecho. Es una
        de las diez reglas que detectan contradicciones.
      </p>
      <Campo
        label="Resumen de tu trayectoria"
        hint="Puestos, sectores, responsabilidades y lo que consideres relevante. Cuanto más concreto, mejor contrasta."
      >
        <Texto
          multiline
          value={String(value.user_notes ?? "")}
          onChange={(v) => save({ user_notes: v })}
          placeholder="Doce años en operaciones industriales, últimos cuatro dirigiendo un equipo de quince personas…"
        />
      </Campo>
      <Campo label="Perfil de LinkedIn" hint="Opcional.">
        <Texto
          value={String(value.linkedin_url ?? "")}
          onChange={(v) => save({ linkedin_url: v })}
          placeholder="https://www.linkedin.com/in/…"
        />
      </Campo>
      <p className="rounded-panel border border-line bg-paper-2 p-3 text-sm text-ink/60">
        La subida de fichero y la extracción automática llegan en la siguiente entrega. De momento
        el resumen escrito cumple la misma función.
      </p>
    </div>
  );
}

// ── 3. Perfil ────────────────────────────────────────────────────────────────

export function PasoProfile({ value, save }: StepProps) {
  return (
    <div className={seccion}>
      <Campo label="Tu nombre">
        <Texto
          value={String(value.first_name ?? "")}
          onChange={(v) => save({ first_name: v })}
          placeholder="Cómo quieres que te llamemos en el informe"
        />
      </Campo>
      <Campo label="Ciudad">
        <Texto value={String(value.city ?? "")} onChange={(v) => save({ city: v })} />
      </Campo>
      <Campo label="País" hint="Código de dos letras.">
        <Texto
          value={String(value.country_iso ?? "ES")}
          onChange={(v) => save({ country_iso: v.toUpperCase().slice(0, 2) })}
        />
      </Campo>
    </div>
  );
}

/**
 * Días fuera al mes ↔ el porcentaje que consume el motor.
 *
 * `compute_feasibility` calcula la rigidez del perímetro como
 * `100 - travel_pct_max`, así que el campo tiene que seguir llegándole en esa
 * escala: cambiar lo que el motor recibe obligaría a subir `SCORING_VERSION` y
 * dejaría de ser reproducible lo ya emitido.
 *
 * El tope de la escala es "15 o más", que quiere decir *sin límite* — un perfil
 * que viaja siempre. Por eso ese extremo vale 100 y no la fracción que le
 * tocaría sobre los días laborables del mes: quien acepta viajar sin tope tiene
 * que poder decirlo.
 */
/**
 * Cuál de las situaciones marcadas es la que ve el motor.
 *
 * `compute_feasibility` penaliza el colchón económico insuficiente cuando la
 * situación es `unemployed`, `founder` o `freelance`, y no cuando es
 * `employed`. La razón de fondo es si entra una nómina: quien cobra por cuenta
 * ajena *y además* factura por su cuenta no está en esa situación de riesgo, así
 * que `employed` manda sobre el resto.
 *
 * Sin marcar nada se devuelve `employed`, que es el valor por defecto que tenía
 * el campo cuando era de opción única.
 */
export function situacionDominante(marcadas: string[]): string {
  if (marcadas.includes("employed")) return "employed";
  return marcadas[0] ?? "employed";
}

/** Lo marcado, aceptando lo guardado antes de que el campo fuera múltiple. */
export function situacionesDe(value: Record<string, any>): string[] {
  if (Array.isArray(value.work_status_list)) return value.work_status_list;
  return value.work_status ? [String(value.work_status)] : ["employed"];
}

const DIAS_MAXIMOS = 15;

export function porcentajeDesdeDias(dias: number): number {
  return Math.min(100, Math.round((dias / DIAS_MAXIMOS) * 100));
}

export function diasDesdePorcentaje(pct: unknown): number {
  const n = Number(pct);
  if (!Number.isFinite(n)) return 3; // el 20 % que era el valor por defecto
  return Math.min(DIAS_MAXIMOS, Math.round((n / 100) * DIAS_MAXIMOS));
}

// ── 4. Situación ─────────────────────────────────────────────────────────────

export function PasoContext({ content, value, save }: StepProps) {
  return (
    <div className={seccion}>
      {/*
          Varias a la vez: se puede estar por cuenta ajena y tener algo propio en
          paralelo, y con una sola casilla esa persona tenía que mentir.

          El motor sigue recibiendo `work_status` en singular porque lo compara
          con `in ("unemployed", "founder", "freelance")` para la penalización
          por colchón económico. Pasarle una lista no daría error: simplemente no
          coincidiría nunca y la penalización dejaría de aplicarse en silencio.
          Se le manda la dominante, que se deriva arriba.
        */}
        <Campo label="Situación laboral actual" hint="Marca todas las que se apliquen.">
          <SeleccionMultiple
            value={situacionesDe(value)}
            onChange={(v) =>
              save({ work_status_list: v, work_status: situacionDominante(v) })
            }
            options={content.catalogs.work_status}
          />
        </Campo>
      <Campo
        label="Horas por semana que puedes dedicar a esto"
        hint="Con menos de una hora, ningún plan es realista. El diagnóstico lo tiene en cuenta."
      >
        <Numero
          value={value.energy_hours_week ?? 4}
          onChange={(v) => save({ energy_hours_week: v })}
          min={0}
          max={40}
          suffix="h/semana"
        />
      </Campo>
      <Campo label="Nivel de estrés actual">
        <Deslizador
          value={Number(value.stress_level_0_10 ?? 5)}
          onChange={(v) => save({ stress_level_0_10: v })}
          leyendaMin="Tranquilo"
          leyendaMax="Desbordado"
        />
      </Campo>
      <Campo label="Preaviso que tendrías que dar">
        <Numero
          value={value.notice_period_weeks ?? 4}
          onChange={(v) => save({ notice_period_weeks: v })}
          min={0}
          max={52}
          suffix="semanas"
        />
      </Campo>
      <Campo
        label="Meses que aguantarías sin ingresos"
        hint="Déjalo en blanco si prefieres no decirlo. No es lo mismo en blanco que cero."
      >
        <Numero
          value={value.runway_months ?? ""}
          onChange={(v) => save({ runway_months: v })}
          min={0}
          max={120}
          suffix="meses"
        />
      </Campo>
    </div>
  );
}

// ── 5. Objetivo ──────────────────────────────────────────────────────────────

export function PasoGoal({ content, value, save }: StepProps) {
  const primaries: string[] = Array.isArray(value.primaries) ? value.primaries : [];
  return (
    <div className={seccion}>
      <Campo
        label="Qué buscas"
        hint="Puedes elegir hasta dos. Más de dos suele ser señal de que aún no está decidido, y eso el diagnóstico lo detecta por otra vía."
      >
        <SeleccionMultiple
          value={primaries}
          onChange={(v) => save({ primaries: v })}
          options={content.catalogs.goals}
          max={2}
        />
      </Campo>
      {/*
        El texto libre solo aparece si se ha marcado "Otro".

        El motor no puede leerlo — no sabría qué intensidad de cambio implica —
        y por eso `other` cae a la intensidad 45 por defecto. El texto se guarda
        con el resto de la sesión y queda disponible para la narrativa cuando
        exista (H6). Sin este campo, "Otro" sería una opción que descarta
        justamente lo que la persona quería decir.
      */}
      {primaries.includes("other") ? (
        <Campo
          label="Describe tu objetivo"
          hint="Con tus palabras. Aparecerá en tu informe tal y como lo escribas."
        >
          <Texto
            multiline
            value={String(value.other_goal ?? "")}
            onChange={(v) => save({ other_goal: v })}
            placeholder="Qué quieres conseguir, y en qué se diferencia de las opciones de arriba"
          />
        </Campo>
      ) : null}
      <Campo
        label="En cuánto tiempo"
        hint="Un cambio de sector en cuatro semanas penaliza la viabilidad: no es pesimismo, es aritmética."
      >
        <Seleccion
          value={String(value.horizon ?? "3m")}
          onChange={(v) => save({ horizon: v })}
          options={content.catalogs.horizons}
        />
      </Campo>
      <Campo label="Cómo sabrás que ha salido bien">
        <Texto
          multiline
          value={String(value.success_definition ?? "")}
          onChange={(v) => save({ success_definition: v })}
          placeholder="Con qué situación concreta te darías por satisfecho dentro de ese plazo"
        />
      </Campo>
    </div>
  );
}

// ── 6. Límites ───────────────────────────────────────────────────────────────

export function PasoConstraints({ content, value, save }: StepProps) {
  const contratos: string[] = Array.isArray(value.contract_type) ? value.contract_type : [];
  const modo = String(value.work_mode ?? "hybrid");
  return (
    <div className={seccion}>
      <p className="text-ink/70">
        Aquí se decide si tu marco permite moverte. Si todo queda cerrado, el diagnóstico te lo
        dirá en vez de buscarte opciones que no existen.
      </p>
      <Campo label="Salario bruto mínimo aceptable">
        <Numero
          value={value.min_salary_gross_year ?? 0}
          onChange={(v) => save({ min_salary_gross_year: v })}
          min={0}
          suffix={String(value.currency ?? "EUR") + " / año"}
        />
      </Campo>
      <Campo label="Modalidad de trabajo">
        <Seleccion
          value={modo}
          onChange={(v) => save({ work_mode: v })}
          options={content.catalogs.work_modes}
        />
      </Campo>
      {modo !== "remote" ? (
        <Campo label="Desplazamiento máximo">
          <Numero
            value={value.max_commute_minutes ?? 45}
            onChange={(v) => save({ max_commute_minutes: v })}
            min={0}
            max={180}
            suffix="minutos"
          />
        </Campo>
      ) : null}
      <Campo label="¿Te reubicarías?">
        <Seleccion
          value={String(value.relocation ?? "no")}
          onChange={(v) => save({ relocation: v })}
          options={content.catalogs.relocation}
        />
      </Campo>
      {/*
          En días, no en porcentaje. "¿Qué porcentaje de tu tiempo aceptarías
          pasar fuera?" no se contesta: nadie lleva ese número en la cabeza y
          cada cual lo interpreta sobre una base distinta.

          Se guarda también la respuesta literal para repintarla al volver al
          paso: reconstruirla desde el porcentaje redondearía, y quien dijo 5
          días se encontraría 4.
        */}
        <Campo
          label="Días al mes que aceptarías pasar fuera de casa por trabajo"
          hint="Cuenta noches fuera, no desplazamientos de ida y vuelta en el día."
        >
          <Deslizador
            value={Number(value.travel_days_max ?? diasDesdePorcentaje(value.travel_pct_max))}
            onChange={(v) =>
              save({ travel_days_max: v, travel_pct_max: porcentajeDesdeDias(v) })
            }
            max={15}
            leyendaMin="Ninguno"
            leyendaMax="15 o más"
          />
        </Campo>
      <Campo
        label="Tipos de contrato que aceptarías"
        hint="Cuantos más admitas, más ancho es tu perímetro real."
      >
        <SeleccionMultiple
          value={contratos}
          onChange={(v) => save({ contract_type: v })}
          options={content.catalogs.contract_types}
        />
      </Campo>
    </div>
  );
}

// ── 7. Prioridades ───────────────────────────────────────────────────────────

export function PasoPreferences({ content, value, save }: StepProps) {
  const [pesos, fijarPesos] = useValorLocal<Record<string, number>>(
    (value.criteria_weights as Record<string, number>) ?? {},
    "preferences",
  );

  // El objeto se reconstruye SIEMPRE en el orden del catálogo. El informe
  // ordena los criterios por peso con un desempate estable, así que ante dos
  // pesos iguales gana el que aparece antes: si el orden dependiera de en qué
  // secuencia movió alguien los deslizadores, dos personas con las mismas
  // respuestas verían criterios distintos en cabeza.
  const actualizar = (key: string, v: number) => {
    const ordenado = fijarPesos((base) => {
      const siguiente: Record<string, number> = {};
      for (const c of content.catalogs.criteria) {
        const valor = c.key === key ? v : base[c.key];
        if (valor !== undefined) siguiente[c.key] = valor;
      }
      return siguiente;
    });
    save({ criteria_weights: ordenado });
  };

  return (
    <div className={seccion}>
      <p className="text-ink/70">
        Qué peso tiene cada cosa para ti. Lo que quede alto es lo que el informe usará para valorar
        escenarios.
      </p>
      {content.catalogs.criteria.map((c) => (
        <Campo key={c.key} label={c.label}>
          <Deslizador
            value={pesos[c.key] ?? 5}
            onChange={(v) => actualizar(c.key, v)}
            min={1}
            max={10}
            leyendaMin="Indiferente"
            leyendaMax="Decisivo"
          />
        </Campo>
      ))}
    </div>
  );
}

// ── 8-10. Instrumentos ───────────────────────────────────────────────────────

export function crearPasoInstrumento(clave: "senior_style" | "holland" | "brakes") {
  return function PasoInstrumento({ content, value, save }: StepProps) {
    const inst = content.instruments[clave];
    if (!inst) return null;
    const escala = escalaDe(content, inst.scale);
    const [respuestas, fijarRespuestas] = useValorLocal<(number | null)[]>(
      Array.isArray(value.responses) ? value.responses : new Array(inst.items.length).fill(null),
      clave,
    );

    const responder = (indice: number, v: number) => {
      const siguientes = fijarRespuestas((actual) => {
        const copia = [...actual];
        copia[indice] = v;
        return copia;
      });
      const completo = siguientes.every((r) => r !== null);
      save({
        responses: siguientes,
        // Se sella al completarlo, como hacía el prototipo. El informe lo
        // muestra y algunas capas lo leen para saber si el instrumento se
        // cerró de verdad.
        ...(completo ? { completed_at: new Date().toISOString() } : {}),
      });
    };

    const contestadas = respuestas.filter((r) => r !== null).length;

    return (
      <div className="space-y-6">
        <div className="flex items-baseline justify-between">
          <p className="text-ink/70">
            Responde con lo primero que te salga. No hay respuestas mejores ni peores.
          </p>
          <span className="shrink-0 text-sm tabular-nums text-ink/50">
            {contestadas} / {inst.items.length}
          </span>
        </div>

        {/*
          Las anclas, visibles y pegadas a la escala.

          Antes la fila de encabezado eran los números pelados y la etiqueta
          vivía solo en `aria-label`: un lector de pantalla la oía, quien
          contesta con la vista no. Y eso no es un detalle de comodidad — quien
          asuma que 1 es "muy característico" contesta toda la batería invertida
          y el motor recibe el dato del revés sin forma de detectarlo.

          Se repiten al pie porque son 32 afirmaciones: el encabezado se pierde
          de vista al segundo desplazamiento.
        */}
        <AnclasEscala opciones={escala.options} instrumento={clave} />

        <ol className="space-y-4">
          {inst.items.map((item, i) => (
            <li
              key={item.id}
              className={
                "rounded-tarjeta border p-4 transition sm:flex sm:items-center sm:gap-4 " +
                (respuestas[i] === null
                  ? "border-line bg-white"
                  : "border-line bg-paper-2")
              }
            >
              <Enunciado>{item.text}</Enunciado>
              <div className="mt-3 sm:mt-0 sm:shrink-0">
                <Escala
                  compact
                  value={respuestas[i] ?? null}
                  onChange={(v) => responder(i, v)}
                  options={escala.options}
                />
              </div>
            </li>
          ))}
        </ol>

        <AnclasEscala opciones={escala.options} instrumento={clave} />
      </div>
    );
  };
}

/**
 * Cómo se lee la escala en cada bloque.
 *
 * Los tres instrumentos comparten `likert_1_5`, cuyas etiquetas son genéricas
 * ("Nada / No me pasa" … "Mucho") porque tienen que servir para los tres. Pero
 * lo que se pregunta no es lo mismo: uno describe cómo eres, otro qué te frena
 * y el tercero qué te interesa, y "Mucho" no ancla igual en los tres.
 *
 * Va aquí y no en el banco porque el banco se exporta del prototipo con
 * `npm run bank` y cualquier edición a mano se perdería en la siguiente
 * exportación. Esto es presentación, no dato del motor.
 */
const ANCLAS: Record<string, { min: string; max: string }> = {
  senior_style: { min: "no me describe", max: "me describe del todo" },
  brakes: { min: "no me pasa", max: "me pasa mucho" },
  holland: { min: "no me interesa", max: "me interesa mucho" },
};

/** Los extremos de una escala Likert, en texto y a la vista. */
function AnclasEscala({
  opciones,
  instrumento,
}: {
  opciones: { v: number; l: string }[];
  instrumento?: string;
}) {
  const primera = opciones[0];
  const ultima = opciones[opciones.length - 1];
  if (!primera || !ultima) return null;

  // Si el bloque no tiene ancla propia, las etiquetas genéricas de la escala
  // siguen siendo mejor que el número solo.
  const ancla = instrumento ? ANCLAS[instrumento] : undefined;
  const min = ancla?.min ?? primera.l.toLowerCase();
  const max = ancla?.max ?? ultima.l.toLowerCase();

  return (
    <div className="flex items-center justify-between gap-4 border-y border-line py-2 text-xs text-ink/60">
      <span>
        <strong className="font-medium tabular-nums text-ink">{primera.v}</strong> = {min}
      </span>
      <span className="text-right">
        <strong className="font-medium tabular-nums text-ink">{ultima.v}</strong> = {max}
      </span>
    </div>
  );
}

// ── 11. Rueda de transición ──────────────────────────────────────────────────

export function PasoWheel({ content, value, save }: StepProps) {
  const escala = escalaDe(content);
  const total = content.wheel_axes.reduce((n, a) => n + a.items.length, 0);
  const [respuestas, fijarRespuestas] = useValorLocal<(number | null)[]>(
    Array.isArray(value.responses) ? value.responses : new Array(total).fill(null),
    "wheel",
  );

  // El índice global es lo que cuenta: el motor recibe una lista plana y
  // reparte por posición. Se calcula recorriendo los ejes en orden, nunca a
  // partir de la clave del eje.
  let cursor = 0;

  return (
    <div className="space-y-8">
      <p className="text-ink/70">
        Seis condiciones que determinan si un cambio es sostenible. No miden lo que quieres, sino
        con qué cuentas para sostenerlo.
      </p>
      {content.wheel_axes.map((eje) => {
        const desde = cursor;
        cursor += eje.items.length;
        return (
          <section key={eje.key} className="rounded-tarjeta border border-line bg-white p-5">
            <header className="mb-4">
              {/* El emoji que trae el motor no se pinta: el manual prohíbe el
                  emoji en copy y en interfaz, y no hay librería de iconos. Lo
                  que identifica al eje es su color, que es la regla del propio
                  manual. El dato del motor se queda donde está; lo que cambia
                  es qué se hace con él. */}
              <h3 className="flex items-center gap-2.5 font-display text-h3 text-ink">
                <span
                  aria-hidden="true"
                  className="h-2 w-2 shrink-0 rounded-full"
                  style={{ backgroundColor: eje.color }}
                />
                {eje.label}
              </h3>
              <p className="text-sm text-ink/60">{eje.description}</p>
            </header>
            <ul className="space-y-4">
              {eje.items.map((texto, j) => {
                const indice = desde + j;
                return (
                  <li key={indice} className="sm:flex sm:items-center sm:gap-4">
                    <Enunciado>{texto}</Enunciado>
                    <div className="mt-2 sm:mt-0 sm:shrink-0">
                      <Escala
                        compact
                        value={respuestas[indice] ?? null}
                        onChange={(v) => {
                          const siguientes = fijarRespuestas((actual) => {
                            const copia = [...actual];
                            copia[indice] = v;
                            return copia;
                          });
                          save({ responses: siguientes });
                        }}
                        options={escala.options}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

// ── 12. Valor movilizable ────────────────────────────────────────────────────

export function PasoValueEvidence({ content, value, save }: StepProps) {
  const escala = escalaDe(content);
  const [respuestas, fijarRespuestas] = useValorLocal<Record<string, number | null>>(
    (value.responses as Record<string, number | null>) ?? {},
    "value_evidence",
  );
  const apoyos: Record<string, unknown> = (value.supports as Record<string, unknown>) ?? {};

  return (
    <div className="space-y-8">
      <p className="text-ink/70">
        No mide lo que vales, sino cuánto de tu valor está disponible hoy: contable, contable a
        otros y visible fuera.
      </p>

      <div className="space-y-4">
        {content.value_questions.map((q) => (
          <div key={q.key} className="rounded-tarjeta border border-line bg-white p-4">
            <p className="font-medium text-ink">{q.label}</p>
            <p className="mt-1 text-sm text-ink/60">{q.hint}</p>
            <div className="mt-3">
              <Escala
                value={respuestas[q.key] ?? null}
                onChange={(v) => {
                  const siguientes = fijarRespuestas((actual) => ({ ...actual, [q.key]: v }));
                  save({ responses: siguientes });
                }}
                options={escala.options}
              />
            </div>
          </div>
        ))}
      </div>

      <Campo
        label="Tu propuesta de valor, en una frase"
        hint="Opcional, pero es lo que después se convierte en tu posicionamiento."
      >
        <Texto
          multiline
          value={String(apoyos.value_pitch ?? "")}
          onChange={(v) => save({ supports: { ...apoyos, value_pitch: v } })}
          placeholder="Ayudo a … a conseguir … mediante …"
        />
      </Campo>
    </div>
  );
}

// ── 13. Palancas ─────────────────────────────────────────────────────────────

export function PasoPalancas({ content, value, save }: StepProps) {
  const [actual, fijarActual] = useValorLocal<Record<string, number>>(
    (value.current as Record<string, number>) ?? {},
    "palancas-current",
  );
  const [deseado, fijarDeseado] = useValorLocal<Record<string, number>>(
    (value.desired as Record<string, number>) ?? {},
    "palancas-desired",
  );

  const fijar = (donde: "current" | "desired", key: string, v: number) => {
    const actualizar = donde === "current" ? fijarActual : fijarDeseado;
    const siguiente = actualizar((base) => ({ ...base, [key]: v }));
    save({ [donde]: siguiente });
  };

  const completas = content.palancas.filter((p) => actual[p.key] !== undefined).length;

  return (
    <div className="space-y-6">
      <div className="flex items-baseline justify-between">
        <p className="text-ink/70">
          Diez palancas sobre las que se puede actuar. Marca dónde estás hoy y dónde necesitarías
          estar: la distancia entre ambas es lo que ordena el plan.
        </p>
        <span className="shrink-0 text-sm tabular-nums text-ink/50">
          {completas} / {content.palancas.length}
        </span>
      </div>

      {content.palancas.map((p) => (
        <section key={p.key} className="rounded-tarjeta border border-line bg-white p-5">
          <h3 className="font-display text-lg text-ink">{p.shortLabel}</h3>
          {p.description ? <p className="mt-1 text-sm text-ink/60">{p.description}</p> : null}
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Campo label="Dónde estás hoy">
              <Deslizador
                value={actual[p.key] ?? 5}
                onChange={(v) => fijar("current", p.key, v)}
                min={1}
                max={10}
              />
            </Campo>
            <Campo label="Dónde necesitarías estar">
              <Deslizador
                value={deseado[p.key] ?? 7}
                onChange={(v) => fijar("desired", p.key, v)}
                min={1}
                max={10}
              />
            </Campo>
          </div>
        </section>
      ))}
    </div>
  );
}

// ── 14. Señales ──────────────────────────────────────────────────────────────

const SENALES: { key: string; label: string; min: string; max: string }[] = [
  { key: "clarity_score_0_10", label: "Claridad sobre lo que quieres", min: "Ninguna", max: "Total" },
  { key: "confidence_score_0_10", label: "Confianza en conseguirlo", min: "Ninguna", max: "Total" },
  { key: "known_options_score_0_10", label: "Opciones que ya conoces", min: "Ninguna", max: "Muchas" },
  { key: "know_how_to_get_info_0_10", label: "Sabes dónde informarte", min: "Nada", max: "Perfectamente" },
  { key: "internal_conflict_0_10", label: "Conflicto interno con la decisión", min: "Ninguno", max: "Enorme" },
  { key: "external_pressure_0_10", label: "Presión de tu entorno", min: "Ninguna", max: "Constante" },
  { key: "motivation_to_act_0_10", label: "Ganas de actuar ahora", min: "Ninguna", max: "Máximas" },
];

export function PasoSignals({ value, save }: StepProps) {
  return (
    <div className={seccion}>
      <p className="text-ink/70">Lo último. Cómo te ves ahora mismo, sin pensarlo demasiado.</p>
      {SENALES.map((s) => (
        <Campo key={s.key} label={s.label}>
          <Deslizador
            value={Number(value[s.key] ?? 5)}
            onChange={(v) => save({ [s.key]: v })}
            leyendaMin={s.min}
            leyendaMax={s.max}
          />
        </Campo>
      ))}
    </div>
  );
}

// ── Registro ─────────────────────────────────────────────────────────────────

export interface StepMeta {
  key: string;
  title: string;
  subtitle: string;
  Component: (props: StepProps) => React.ReactElement | null;
}

export const STEP_META: Record<string, Omit<StepMeta, "key">> = {
  consent: { title: "Permisos", subtitle: "Qué autorizas y durante cuánto tiempo", Component: PasoConsent },
  cv: { title: "Tu trayectoria", subtitle: "De dónde vienes", Component: PasoCv },
  profile: { title: "Quién eres", subtitle: "Lo mínimo para dirigirnos a ti", Component: PasoProfile },
  context: { title: "Tu situación", subtitle: "Con qué cuentas ahora mismo", Component: PasoContext },
  goal: { title: "Tu objetivo", subtitle: "Qué buscas y en cuánto tiempo", Component: PasoGoal },
  constraints: { title: "Tus límites", subtitle: "Qué no estás dispuesto a mover", Component: PasoConstraints },
  preferences: { title: "Tus prioridades", subtitle: "Qué pesa más en tu decisión", Component: PasoPreferences },
  senior_style: {
    title: "Estilo profesional",
    subtitle: "32 afirmaciones sobre cómo trabajas",
    Component: crearPasoInstrumento("senior_style"),
  },
  holland: {
    title: "Intereses",
    subtitle: "48 afirmaciones sobre qué te atrae",
    Component: crearPasoInstrumento("holland"),
  },
  brakes: {
    title: "Frenos",
    subtitle: "30 afirmaciones sobre qué te detiene",
    Component: crearPasoInstrumento("brakes"),
  },
  wheel: { title: "Rueda de transición", subtitle: "Seis condiciones del cambio", Component: PasoWheel },
  value_evidence: {
    title: "Valor movilizable",
    subtitle: "Cuánto de tu valor está disponible hoy",
    Component: PasoValueEvidence,
  },
  palancas: { title: "Palancas", subtitle: "Dónde estás y dónde necesitas estar", Component: PasoPalancas },
  signals: { title: "Señales", subtitle: "Cómo te ves ahora mismo", Component: PasoSignals },
};
